import { Divider, Switch, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import EditIconOutline from 'assets/img/EditIconOutline';
import {
  blackColor,
  cadetBlueGrayColor,
  dangerColor,
  grayColor,
  navyColor,
  primaryColor,
  successColor,
  warningColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button/Button';
import { useSelector } from 'react-redux';
import { currencyToSymbol } from 'utils/currencies';
import classNames from 'classnames';
import SelectInput from 'components/FormSelect/SelectInput';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import EXTERNAL_URLS from 'constants/externalUrls';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import SubscriptionPause from 'views/Orders/Subscriptions/Dialogs/SubscriptionPause';
import SubscriptionMarkToRemove from 'views/Orders/Subscriptions/Dialogs/SubscriptionMarkToRemove';
import SubscriptionHistory from 'views/Orders/Subscriptions/Dialogs/SubscriptionHistory';
import SubscriptionOrders from 'views/Orders/Subscriptions/Dialogs/SubscriptionOrders';

import DietDetailsDietName from '../DietDetailsDietName';

const formatDietDate = date => (date ? moment(date).format('DD.MM') : '-');

const getDietDateRange = (firstDeliveryDate, lastDeliveryDate) =>
  firstDeliveryDate || lastDeliveryDate
    ? `${formatDietDate(firstDeliveryDate)} - ${formatDietDate(
        lastDeliveryDate
      )}`
    : '-';

const useStyles = makeStyles({
  wrapper: {
    background: grayColor[13],
    borderRadius: '6px',
    padding: '16px',
    fontWeight: '400',
    fontSize: '14px',
    marginBottom: '16px',
    minWidth: '280px',
  },
  editSectionWrapper: { display: 'flex', justifyContent: 'space-between' },
  editSectionLabel: { fontSize: '12px', color: grayColor[1] },
  tooltipSectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dietSectionLabel: {
    fontSize: '12px',
    color: grayColor[1],
  },
  itemWrapper: { display: 'flex', justifyContent: 'space-between' },
  itemTitle: {
    fontWeight: '500',
    color: grayColor[16],
    marginBottom: '8px',
  },
  itemValue: {
    color: grayColor[1],
    marginBottom: '16px',
  },
  idTag: {
    marginRight: '8px',
  },
  properNameWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  properName: { color: grayColor[1] },
  wrapperRedirectOtherDiet: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  button: { padding: '0', minWidth: '28px' },
  redirectButton: {
    color: primaryColor[0],
    fontWeight: '400',
    padding: '0',
    marginBottom: '8px',
    textAlign: 'left',
  },
  editIcon: { cursor: 'pointer' },
  discountItemValue: {
    color: grayColor[1],
    cursor: 'pointer',
    marginLeft: '16px',
    marginBottom: '4px',
  },
  discountSummaryWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  lastItemValue: { color: grayColor[1], marginBottom: '0' },
  divider: {
    marginBottom: '16px',
  },
  infoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '16px',
  },
  status: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontWeight: '500',
  },
  orderInfoItem: {
    padding: '4px 8px',
    borderRadius: '4px',
    marginRight: '8px',
    fontWeight: '500',
  },
  successStatus: {
    color: successColor[4],
    background: successColor[6],
  },
  warningStatus: {
    color: warningColor[4],
    background: warningColor[6],
  },
  dangerStatus: {
    color: dangerColor[4],
    background: dangerColor[6],
  },
  selectTitle: {
    fontWeight: '500',
    color: grayColor[16],
    margin: '0',
  },
  optionSelectOrderId: {
    fontSize: '14px',
    fontWeight: '500',
    background: grayColor[13],
    padding: '4px 8px',
    borderRadius: '4px',
  },
  optionSelectDescription: {
    fontSize: '14px',
    fontWeight: '400',
    marginLeft: '8px',
  },
  optionSelectDescriptionActive: { color: primaryColor[1], fontWeight: '500' },
  presentationSelectedItemId: {
    display: 'inline-flex',
    alignItems: 'center',
    background: whiteColor,
    color: blackColor,
    padding: '4px 8px',
    borderRadius: '4px',
    fontWeight: '500',
  },
  customSelectMenuItem: {
    padding: '4px 6px',
    margin: '4px 0',
    '&:hover span:first-of-type': {
      color: blackColor,
    },
    '&:hover span:last-of-type': {
      color: whiteColor,
    },
    '&:hover': { backgroundColor: primaryColor[1], color: whiteColor },
  },
  customSwitchBase: {
    height: 24,
    color: grayColor[0],
    '& + $customBar': {
      backgroundColor: grayColor[5],
      opacity: 1,
    },
    '&$customChecked + $customBar': {
      backgroundColor: cadetBlueGrayColor,
      opacity: 1,
    },
  },
  customChecked: { color: navyColor },
  customBar: {
    borderRadius: 9999,
    opacity: 1,
  },
  subscriptionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px',
  },
  subscriptionLabel: {
    marginRight: '8px',
  },
  subscriptionButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  subscriptionTextButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '14px',
  },
});

const DietDetailLeftSection = ({
  dietInfo,
  handleSave,
  orderInfo,
  allDiets,
  setDietId,
  fetchOnDietChange,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const currency = useSelector(
    ({ Brands }) => Brands.brand.multinational.currency
  );
  const [isOpenDietNameModal, setIsOpenDietNameModal] = useState(false);
  const [isOnlyActive, setOnlyActive] = useState(false);

  const currencySymbol = currencyToSymbol(currency);
  const currentDietDateRange = getDietDateRange(
    dietInfo.firstDeliveryDate,
    dietInfo.lastDeliveryDate
  );
  const dietDuration = dietInfo.dietLength;

  const discountCode = dietInfo?.usedDiscounts?.CODE?.discount || 0;
  const discountDietLength =
    dietInfo?.usedDiscounts?.DIET_LENGTH?.discount || 0;
  const discountPickUpPoint =
    dietInfo?.usedDiscounts?.PICK_UP_POINT?.discount || 0;
  const discountSameAddress =
    dietInfo?.usedDiscounts?.SAME_ADDRESS?.discount || 0;
  const discountFreeDelivery =
    dietInfo?.usedDiscounts?.FREE_DELIVERY?.discount || 0;

  const [currentDiet] = allDiets.filter(
    dietItem => dietItem['@id'] === dietInfo['@id']
  );

  const otherDiet = orderInfo.items.filter(
    diet =>
      diet['@type'] === 'OrderItemDiet' && diet.diet['@id'] !== dietInfo['@id']
  );

  const someDiscountIsExist = dietInfo?.priceDiscount > 0;

  const dietsToShow = isOnlyActive
    ? allDiets.filter(diet => diet.active)
    : allDiets;

  const activeDiet = {
    id: dietInfo.id,
    value: dietInfo['@id'],
  };

  const noActiveDiets = isOnlyActive && dietsToShow.length === 0;

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.editSectionWrapper}>
          <p
            className={classNames(
              classes.editSectionLabel,
              classes.tooltipSectionWrapper
            )}
          >
            {t('orders.editDiet')}{' '}
            <InfoWithTooltip
              title={t('orders.tooltip.whyDietStillActive')}
              link={EXTERNAL_URLS.whyEndedDietIsStillActive}
            />
          </p>
          <p className={classes.editSectionLabel}>
            {t('orders.onlyActive')}
            <Switch
              color="default"
              classes={{
                switchBase: classes.customSwitchBase,
                checked: classes.customChecked,
                bar: classes.customBar,
              }}
              checked={isOnlyActive}
              onChange={() => setOnlyActive(prev => !prev)}
            />
          </p>
        </div>
        {noActiveDiets ? (
          <p>{t('orders.noActiveDiets')}</p>
        ) : (
          <SelectInput
            label={t('orders.selectedDiet')}
            classes={{ formLabel: classes.selectTitle }}
            mapBy="label"
            trackBy="value"
            placeholder={t('common.dietPrices.selectDiet')}
            restyledRootMenuItem={classes.customSelectMenuItem}
            restyledRenderValue={({ selected }) => {
              const selectedItem = allDiets.find(
                diet => diet['@id'] === selected
              );

              const dietDateRange = getDietDateRange(
                selectedItem.firstDeliveryDate,
                selectedItem.lastDeliveryDate
              );

              return (
                <div>
                  <span
                    className={classNames(classes.presentationSelectedItemId)}
                  >
                    #{selectedItem.id}
                  </span>
                  <span
                    className={classNames(
                      classes.optionSelectDescription,
                      classes.optionSelectDescriptionActive
                    )}
                  >
                    {`${
                      selectedItem.diet?.name || selectedItem.name
                    } ${dietDateRange}`}
                  </span>
                </div>
              );
            }}
            options={[...dietsToShow]
              .sort((a, b) => {
                if (a.active !== b.active) {
                  return Number(b.active) - Number(a.active);
                }

                return Number(b.id) - Number(a.id);
              })
              .map(
                ({
                  id,
                  '@id': iri,
                  name,
                  active,
                  diet,
                  firstDeliveryDate,
                  lastDeliveryDate,
                }) => {
                  const dietDateRange = getDietDateRange(
                    firstDeliveryDate,
                    lastDeliveryDate
                  );

                  return {
                    id,
                    value: iri,
                    label: (
                      <div>
                        <span className={classes.optionSelectOrderId}>
                          #{id}
                        </span>
                        <span
                          className={classNames(
                            classes.optionSelectDescription,
                            active && classes.optionSelectDescriptionActive
                          )}
                        >{`${diet?.name || name} ${dietDateRange}`}</span>
                      </div>
                    ),
                  };
                }
              )}
            value={activeDiet}
            handleChange={(e, obj) => {
              setDietId(obj.id);
              fetchOnDietChange(obj.id);
            }}
            size={12}
          />
        )}
      </div>
      <div className={classes.wrapper}>
        <p className={classes.dietSectionLabel}>{t('orders.dietDetails')}</p>
        <p className={classes.itemTitle}>{t('orders.status')}</p>
        <div className={classes.infoWrapper}>
          <div className={classNames(classes.presentationSelectedItemId)}>
            <span className={classes.idTag}>ID #{dietInfo.id}</span>
            <CopyToClipboard text={String(dietInfo.id)} />
          </div>
          {currentDiet.active ? (
            <div className={classNames(classes.status, classes.successStatus)}>
              {t('orders.status.active')}
            </div>
          ) : (
            <div className={classNames(classes.status, classes.dangerStatus)}>
              {t('orders.status.deactivated')}
            </div>
          )}
        </div>
        {dietInfo.diet && dietInfo.variant && dietInfo.calorific && (
          <>
            <p className={classes.itemTitle}>{t('orders.kind')}</p>
            <p className={classes.itemValue}>
              {dietInfo.diet?.name || dietInfo.name}
            </p>
          </>
        )}
        {dietInfo.variant && (
          <>
            <p className={classes.itemTitle}>{t('orders.variant')}</p>
            <p className={classes.itemValue}>{dietInfo.variant.name}</p>
          </>
        )}
        {dietInfo.calorific && (
          <>
            <p className={classes.itemTitle}>{t('orders.calorific')}</p>
            <p className={classes.itemValue}>{dietInfo.calorific.name}</p>
          </>
        )}
        <Divider className={classes.divider} />
        <p className={classes.itemTitle}>{t('orders.duration')}</p>
        {dietInfo.subscription ? (
          <>
            <div className={classes.subscriptionWrapper}>
              <div>
                <span className={classes.subscriptionLabel}>
                  {t('common.subscription')}
                </span>
                {dietInfo.subscription.markedToRemove && (
                  <div
                    className={classNames(classes.status, classes.dangerStatus)}
                  >
                    {t('common.shared.disabled')}
                  </div>
                )}
                {!dietInfo.subscription.active &&
                  !dietInfo.subscription.markedToRemove && (
                    <div
                      className={classNames(
                        classes.status,
                        classes.warningStatus
                      )}
                    >
                      {t('orders.status.suspended')}
                    </div>
                  )}
                {dietInfo.subscription.active && (
                  <div
                    className={classNames(
                      classes.status,
                      classes.successStatus
                    )}
                  >
                    {t('orders.status.active')}
                  </div>
                )}
              </div>
              <div className={classes.subscriptionButtonsWrapper}>
                {!dietInfo?.subscription.markedToRemove && (
                  <SubscriptionPause
                    subscription={dietInfo?.subscription}
                    onUpdate={() => fetchOnDietChange(dietInfo.id)}
                  />
                )}
                <SubscriptionMarkToRemove
                  subscription={dietInfo?.subscription}
                  onUpdate={() => fetchOnDietChange(dietInfo.id)}
                />
              </div>
            </div>
            <div className={classes.subscriptionTextButtons}>
              <SubscriptionHistory row={dietInfo.subscription} />
              <SubscriptionOrders subscription={dietInfo.subscription} />
            </div>
          </>
        ) : (
          <p
            className={classes.itemValue}
          >{`${currentDietDateRange} (${dietDuration} ${t('orders.days')})`}</p>
        )}
        <Divider className={classes.divider} />
        <p className={classes.itemTitle}>{t('orders.order')}</p>
        {orderInfo.isTest && (
          <p className={classes.itemValue}>{t('common.trial')}</p>
        )}
        <div className={classes.infoWrapper}>
          <div className={classes.presentationSelectedItemId}>
            <span className={classes.idTag}>ID #{dietInfo.order.id}</span>
            <CopyToClipboard text={String(dietInfo.order.id)} />
          </div>
          {orderInfo.status.systemValue === 'PAID' && (
            <span
              className={classNames(
                classes.orderInfoItem,
                classes.successStatus
              )}
            >
              {t('orders.status.paid')}
            </span>
          )}
          {orderInfo.status.systemValue === 'CANCELED' && (
            <span
              className={classNames(
                classes.orderInfoItem,
                classes.dangerStatus
              )}
            >
              {t('orders.status.cancelled')}
            </span>
          )}
        </div>
        {otherDiet.length > 0 && (
          <div className={classes.wrapperRedirectOtherDiet}>
            <p className={classes.itemTitle}>
              {t('orders.otherDietsFromSameOrder')}
            </p>
            {otherDiet.map(item => (
              <Button
                className={classes.redirectButton}
                onClick={() => {
                  setDietId(item.diet.id);
                  fetchOnDietChange(item.diet.id);
                }}
                key={item['@id']}
              >{`(#${item.diet.id}) ${item.diet.diet.name} ${moment(
                item.days[0]
              ).format('DD.MM')} - ${moment(
                item.days[item.days.length - 1]
              ).format('DD.MM')}`}</Button>
            ))}
          </div>
        )}
        <Divider className={classes.divider} />
        <p className={classes.itemTitle}>{t('orders.properName')}</p>
        <div className={classes.properNameWrapper}>
          <span className={classes.properName}>{dietInfo.name || '-'}</span>
          <Button
            className={classes.button}
            onClick={() => setIsOpenDietNameModal(true)}
          >
            <EditIconOutline fill={grayColor[1]} className={classes.editIcon} />
          </Button>
        </div>
        <Divider className={classes.divider} />
        <p
          className={classNames(
            classes.itemTitle,
            classes.tooltipSectionWrapper
          )}
        >
          {t('orders.currentDietValue')}
          <InfoIconTooltip title={t('orders.tooltip.currentDietValue')} />
        </p>
        <div className={classes.itemWrapper}>
          <p className={classes.itemValue}>{t('orders.beforeDiscount')}</p>
          <p className={classes.itemValue}>
            {`${
              dietInfo.priceBeforeDiscount !== null &&
              dietInfo.priceBeforeDiscount !== ''
                ? Number(dietInfo.priceBeforeDiscount).toFixed(2)
                : '-'
            } ${currencySymbol}`}
          </p>
        </div>
        {someDiscountIsExist && (
          <>
            <p className={classes.itemValue}>{t('orders.discounts')}:</p>
            <div>
              {discountCode > 0 && (
                <Tooltip
                  title={`${t('orders.usedDiscountCode')} - ${
                    dietInfo?.usedDiscounts?.CODE?.payload?.code
                  }`}
                  placement="right"
                >
                  <div className={classes.itemWrapper}>
                    <p className={classes.discountItemValue}>
                      {t('orders.discountCode')}
                    </p>
                    <p
                      className={classes.discountItemValue}
                    >{`${discountCode.toFixed(2)} ${currencySymbol}`}</p>
                  </div>
                </Tooltip>
              )}
              {discountDietLength > 0 && (
                <div className={classes.itemWrapper}>
                  <p className={classes.discountItemValue}>
                    {t('orders.discountDietLength')}
                  </p>
                  <p
                    className={classes.discountItemValue}
                  >{`${discountDietLength.toFixed(2)} ${currencySymbol}`}</p>
                </div>
              )}
              {discountPickUpPoint > 0 && (
                <div className={classes.itemWrapper}>
                  <p className={classes.discountItemValue}>
                    {t('orders.discountPickUpPoint')}
                  </p>
                  <p
                    className={classes.discountItemValue}
                  >{`${discountPickUpPoint.toFixed(2)} ${currencySymbol}`}</p>
                </div>
              )}
              {discountSameAddress > 0 && (
                <div className={classes.itemWrapper}>
                  <p className={classes.discountItemValue}>
                    {t('orders.discountSameAddress')}
                  </p>
                  <p
                    className={classes.discountItemValue}
                  >{`${discountSameAddress.toFixed(2)} ${currencySymbol}`}</p>
                </div>
              )}
              {discountFreeDelivery > 0 && (
                <div className={classes.itemWrapper}>
                  <p className={classes.discountItemValue}>
                    {t('orders.discountFreeDelivery')}
                  </p>
                  <p
                    className={classes.discountItemValue}
                  >{`${discountFreeDelivery.toFixed(2)} ${currencySymbol}`}</p>
                </div>
              )}
            </div>
            <div className={classes.discountSummaryWrapper}>
              <p className={classes.itemValue}>{t('orders.discountsTotal')}</p>
              <p className={classes.itemValue}>{`${Number(
                dietInfo?.priceDiscount ?? 0
              ).toFixed(2)} ${currencySymbol}`}</p>
            </div>
            <div className={classes.itemWrapper}>
              <p className={classes.lastItemValue}>
                {t('orders.afterDiscount')}
              </p>
              <p className={classes.lastItemValue}>
                {`${Number(dietInfo?.priceAfterDiscount ?? 0).toFixed(
                  2
                )} ${currencySymbol}`}
              </p>
            </div>
          </>
        )}
      </div>
      <DietDetailsDietName
        dietInfo={dietInfo}
        handleSave={handleSave}
        isOpen={isOpenDietNameModal}
        setIsOpen={setIsOpenDietNameModal}
      />
    </>
  );
};

export default DietDetailLeftSection;
